import { createClient } from '@supabase/supabase-js'
import type { Participant, Event, MediaItem } from '@/types'

const url  = import.meta.env.VITE_SUPABASE_URL  as string
const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string

if (!url || !anon) {
  console.error('[Supabase] Missing env vars — check .env')
}

export const supabase = createClient(url, anon, {
  auth: {
    persistSession:     true,
    autoRefreshToken:   true,
    detectSessionInUrl: true,
  },
  realtime: {
    params: { eventsPerSecond: 10 },
  },
})

// ─── Participants ──────────────────────────────────────────────────────────────

export async function fetchParticipants(): Promise<Participant[]> {
  const { data, error } = await supabase
    .from('participants')
    .select('id, name, handle, role, avatar_url, created_at')
    .order('name')
  if (error) throw new Error(`fetchParticipants: ${error.message}`)
  return data ?? []
}

export async function upsertParticipant(
  p: Pick<Participant, 'name' | 'role'> & { user_id?: string }
): Promise<Participant> {
  const { data, error } = await supabase
    .from('participants')
    .upsert(p, { onConflict: 'handle' })
    .select()
    .single()
  if (error) throw new Error(`upsertParticipant: ${error.message}`)
  return data
}

// ─── Events ───────────────────────────────────────────────────────────────────

export async function fetchEvents(): Promise<Event[]> {
  const { data, error } = await supabase
    .from('club_events')
    .select('id, name, location, venue, starts_at, ends_at, tags, created_at')
    .order('starts_at', { ascending: false })
  if (error) throw new Error(`fetchEvents: ${error.message}`)
  return data ?? []
}

// ─── Media items ──────────────────────────────────────────────────────────────

export async function saveMediaItem(
  item: Pick<MediaItem,
    'cloudinary_url' | 'cloudinary_id' | 'media_type' |
    'file_name' | 'file_size' | 'participant_id' | 'event_id'
  >
): Promise<MediaItem> {
  const { data, error } = await supabase
    .from('media_items')
    .insert({ ...item, status: 'pending' })
    .select()
    .single()
  if (error) throw new Error(`saveMediaItem: ${error.message}`)
  return data
}

export async function updateMediaAI(
  id: string,
  ai: {
    tags: string[]
    description: string
    mood: string
    route_score: number
    grade?: string
    editor_note?: string
    flags?: string[]
    hold_for_review?: boolean
    technical_scores?: Record<string, number>
    editorial_scores?: Record<string, number>
    commercial_scores?: Record<string, number>
    avg_technical?: number
    avg_editorial?: number
    avg_commercial?: number
    suggested_audiences?: string[]
  }
): Promise<void> {
  const { error } = await supabase
    .from('media_items')
    .update({
      ai_tags:               ai.tags,
      ai_description:        ai.description,
      ai_mood:               ai.mood,
      ai_route_score:        ai.route_score,
      ai_grade:              ai.grade,
      ai_editor_note:        ai.editor_note,
      ai_flags:              ai.flags ?? [],
      ai_hold:               ai.hold_for_review ?? false,
      ai_technical_scores:   ai.technical_scores,
      ai_editorial_scores:   ai.editorial_scores,
      ai_commercial_scores:  ai.commercial_scores,
      ai_avg_technical:      ai.avg_technical,
      ai_avg_editorial:      ai.avg_editorial,
      ai_avg_commercial:     ai.avg_commercial,
      ai_suggested_audiences: ai.suggested_audiences ?? [],
      status:                'processed',
      processed_at:          new Date().toISOString(),
    })
    .eq('id', id)
  if (error) throw new Error(`updateMediaAI: ${error.message}`)
}

export async function fetchRecentMedia(limit = 24): Promise<MediaItem[]> {
  const { data, error } = await supabase
    .from('media_items')
    .select(`
      *,
      participant:participants(name, handle, role),
      event:club_events(name, venue)
    `)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw new Error(`fetchRecentMedia: ${error.message}`)
  return data ?? []
}

export async function fetchDashboardStats() {
  const { data, error } = await supabase
    .from('media_items')
    .select('status, ai_route_score, ai_mood, media_type, created_at')
  if (error) throw new Error(`fetchDashboardStats: ${error.message}`)

  const all = data ?? []
  const processed = all.filter(i => i.status === 'processed' || i.status === 'routed')
  const scores = processed.map(i => i.ai_route_score).filter(Boolean) as number[]
  const moods = processed.map(i => i.ai_mood).filter(Boolean) as string[]
  const moodMap: Record<string, number> = {}
  moods.forEach(m => { moodMap[m] = (moodMap[m] ?? 0) + 1 })
  const topMood = Object.entries(moodMap).sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—'

  return {
    total:        all.length,
    processed:    processed.length,
    avgScore:     scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0,
    topMood,
    byType: {
      photo:    all.filter(i => i.media_type === 'photo').length,
      video:    all.filter(i => i.media_type === 'video').length,
      audio:    all.filter(i => i.media_type === 'audio').length,
    },
    today: all.filter(i => {
      const d = new Date(i.created_at)
      const now = new Date()
      return d.toDateString() === now.toDateString()
    }).length,
  }
}

// ── New helpers for 1804va ────────────────────────────────────

export async function saveRightsProfile(mediaItemId: string, rights: {
  usage_type: string
  credit_required: boolean
  embargo_until: string | null
  allowed_audiences: string[]
}) {
  const { error } = await supabase
    .from('rights_profiles')
    .upsert({ media_item_id: mediaItemId, ...rights }, { onConflict: 'media_item_id' })
  if (error) throw new Error(`saveRightsProfile: ${error.message}`)
}

export async function triggerPushEngine(mediaItemId: string) {
  const { data, error } = await supabase.functions.invoke('push-engine', {
    body: { media_item_id: mediaItemId },
  })
  if (error) console.error('push-engine invoke error:', error.message)
  return data
}

export async function ensureRecipient(participantId: string, interestTags: string[] = [], preferredTypes: string[] = ['photo', 'video']) {
  const { data: existing } = await supabase
    .from('recipients')
    .select('id')
    .eq('participant_id', participantId)
    .single()

  if (existing) return existing

  const { data, error } = await supabase
    .from('recipients')
    .insert({ participant_id: participantId, interest_tags: interestTags, preferred_types: preferredTypes })
    .select('id')
    .single()
  if (error) throw new Error(`ensureRecipient: ${error.message}`)
  return data
}

export async function fetchInboxCount(recipientId: string): Promise<number> {
  const { count } = await supabase
    .from('push_queue')
    .select('id', { count: 'exact', head: true })
    .eq('recipient_id', recipientId)
    .eq('status', 'queued')
  return count ?? 0
}

// ── 2004va: Session-aware media save ──────────────────────────────────────────
// Saves media with held status if participant is anonymous and no contact captured.
// Push engine must not receive held items — route only after identity release.

export async function saveMediaItemWithSession(
  item: Pick<MediaItem,
    'cloudinary_url' | 'cloudinary_id' | 'media_type' |
    'file_name' | 'file_size' | 'participant_id' | 'event_id'
  > & {
    shoot_session_id?: string
    is_anonymous?: boolean
    contact_captured?: boolean
  }
): Promise<MediaItem> {
  const { shoot_session_id, is_anonymous, contact_captured, ...rest } = item

  // Anonymous with no contact → held. Everything else → pending.
  const status = (is_anonymous && !contact_captured) ? 'held' : 'pending'

  const { data, error } = await supabase
    .from('media_items')
    .insert({ ...rest, status, shoot_session_id: shoot_session_id ?? null })
    .select()
    .single()

  if (error) throw new Error(`saveMediaItemWithSession: ${error.message}`)

  // Increment session media count
  if (shoot_session_id) {
    await supabase.rpc('increment_session_media_count', { p_session_id: shoot_session_id })
  }

  return data
}

// ── Release held media when participant provides contact ──────────────────────
export async function releaseHeldMedia(participantId: string): Promise<number> {
  const { data, error } = await supabase
    .rpc('release_held_media', { p_participant_id: participantId })
  if (error) throw new Error(`releaseHeldMedia: ${error.message}`)
  return data ?? 0
}

// ── Fetch active event for a participant ──────────────────────────────────────
export async function fetchParticipantActiveEvent(participantId: string): Promise<Event | null> {
  const { data: p } = await supabase
    .from('participants')
    .select('active_event_id')
    .eq('id', participantId)
    .single()

  if (!p?.active_event_id) return null

  const { data: ev } = await supabase
    .from('club_events')
    .select('id, name, location, venue, starts_at, ends_at, tags, created_at')
    .eq('id', p.active_event_id)
    .single()

  return ev ?? null
}

// ── 2004va: Creator challenge ─────────────────────────────────────────────────
// Photographer pushes back on AI verdict with context.
// Creates challenge record, calls claude-router with context, updates media item.

export async function submitCreatorChallenge(params: {
  mediaItemId:      string
  participantId:    string
  challengeNote:    string
  originalGrade:    string
  originalScore:    number
  originalNote:     string
  cloudinaryUrl:    string
  mediaType:        string
  fileName:         string
  eventName?:       string
  sessionBrief?:    string
}): Promise<{
  revised_grade: string
  revised_score: number
  revised_note:  string
  challenge_accepted: boolean
}> {
  // 1. Insert challenge record
  const { data: challenge, error: cErr } = await supabase
    .from('creator_challenges')
    .insert({
      media_item_id:  params.mediaItemId,
      participant_id: params.participantId,
      challenge_note: params.challengeNote,
      original_grade: params.originalGrade,
      original_score: params.originalScore,
      original_note:  params.originalNote,
      status:         'pending',
    })
    .select('id')
    .single()

  if (cErr) throw new Error(`submitCreatorChallenge insert: ${cErr.message}`)

  // Mark media item as challenge pending
  await supabase
    .from('media_items')
    .update({ challenge_status: 'pending', challenge_count: params.originalScore })
    .eq('id', params.mediaItemId)

  // 2. Call claude-router with challenge context
  const url  = import.meta.env.VITE_SUPABASE_URL as string
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string

  const res = await fetch(`${url}/functions/v1/claude-router`, {
    method: 'POST',
    headers: {
      'Content-Type':  'application/json',
      'apikey':        anon,
      'Authorization': `Bearer ${anon}`,
    },
    body: JSON.stringify({
      cloudinary_url:    params.cloudinaryUrl,
      media_type:        params.mediaType,
      file_name:         params.fileName,
      event_name:        params.eventName,
      session_brief:     params.sessionBrief,
      challenge_context: params.challengeNote,
      original_grade:    params.originalGrade,
      original_note:     params.originalNote,
    }),
  })

  if (!res.ok) throw new Error(`claude-router challenge re-eval: ${res.status}`)
  const ai = await res.json()

  // 3. Update challenge record with verdict
  await supabase
    .from('creator_challenges')
    .update({
      revised_grade:  ai.grade,
      revised_score:  ai.route_score,
      revised_note:   ai.editor_note,
      status:         ai.challenge_accepted ? 'accepted' : 'rejected',
      resolved_at:    new Date().toISOString(),
    })
    .eq('id', challenge.id)

  // 4. If accepted — update media item with revised scores
  if (ai.challenge_accepted) {
    await supabase
      .from('media_items')
      .update({
        ai_grade:        ai.grade,
        ai_route_score:  ai.route_score,
        ai_editor_note:  ai.editor_note,
        ai_flags:        ai.flags ?? [],
        ai_hold:         ai.hold_for_review ?? false,
        challenge_status: 'revised',
        status:           ai.route_score >= 0.6 ? 'pending' : 'processed',
      })
      .eq('id', params.mediaItemId)
  } else {
    await supabase
      .from('media_items')
      .update({ challenge_status: 'none' })
      .eq('id', params.mediaItemId)
  }

  return {
    revised_grade:      ai.grade,
    revised_score:      ai.route_score,
    revised_note:       ai.editor_note,
    challenge_accepted: ai.challenge_accepted ?? false,
  }
}

// ── 2004va: Feed fetches ───────────────────────────────────────────────────────

// Creator feed — their own shots only, includes held, ordered newest first
export async function fetchCreatorFeed(participantId: string, limit = 48): Promise<MediaItem[]> {
  const { data, error } = await supabase
    .from('media_items')
    .select(`
      *,
      participant:participants(name, handle, role),
      event:club_events(name, venue)
    `)
    .eq('participant_id', participantId)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw new Error(`fetchCreatorFeed: ${error.message}`)
  return data ?? []
}

// Marketplace feed — routed/processed shots only, excludes held (identity or AI)
// Used by buyers, press, organisers
export async function fetchMarketplaceFeed(limit = 48): Promise<MediaItem[]> {
  const { data, error } = await supabase
    .from('media_items')
    .select(`
      *,
      participant:participants(name, handle, role),
      event:club_events(name, venue)
    `)
    .in('status', ['processed', 'routed'])
    .gte('ai_route_score', 0.6)
    .order('ai_route_score', { ascending: false })
    .limit(limit)
  if (error) throw new Error(`fetchMarketplaceFeed: ${error.message}`)
  return data ?? []
}

// Release a held item to market — sets status to pending, triggers push engine
export async function releaseMediaToMarket(mediaItemId: string): Promise<void> {
  const { error } = await supabase
    .from('media_items')
    .update({ status: 'pending' })
    .eq('id', mediaItemId)
  if (error) throw new Error(`releaseMediaToMarket: ${error.message}`)
}

// ── Platform config ───────────────────────────────────────────────────────────
// Single-row config. Cached — changes require page reload or cache invalidation.

export interface PlatformConfig {
  charging_enabled:  boolean
  platform_fee_pct:  number
  evaluation_mode:   boolean
  evaluation_label:  string
  free_roles:        string[]
}

export async function fetchPlatformConfig(): Promise<PlatformConfig> {
  const { data, error } = await supabase
    .from('platform_config')
    .select('charging_enabled, platform_fee_pct, evaluation_mode, evaluation_label, free_roles')
    .eq('id', 1)
    .single()

  if (error || !data) {
    // Safe default — always free if config missing
    return {
      charging_enabled: false,
      platform_fee_pct: 5,
      evaluation_mode:  true,
      evaluation_label: 'Evaluation',
      free_roles:       ['organiser', 'sponsor'],
    }
  }
  return data as PlatformConfig
}

// Returns the platform fee for a transaction.
// Always 0 during evaluation. Always 0 for exempt roles.
export function calculatePlatformFee(
  price: number,
  config: PlatformConfig,
  buyerRole?: string
): number {
  if (!config.charging_enabled)  return 0
  if (config.evaluation_mode)    return 0
  if (buyerRole && config.free_roles.includes(buyerRole)) return 0
  return Math.round(price * (config.platform_fee_pct / 100) * 100) / 100
}

// Records ToS acceptance for a participant — called on entry.
export async function acceptTos(participantId: string, version = '1.0'): Promise<void> {
  const { error } = await supabase.rpc('accept_tos', {
    p_participant_id: participantId,
    p_version:        version,
  })
  if (error) throw error
}
